import React from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, Users, Briefcase, FileSpreadsheet, UserCog, Shield, DollarSign, FileText, Key, Wallet, CreditCard, FileBarChart, UserCheck, Package, Receipt } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { PendenciaDot } from "./pendenciaDocsContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/operacoes", label: "Operações", icon: FileSpreadsheet, testid: "nav-operacoes" },
  { to: "/recebiveis", label: "Recebíveis", icon: FileBarChart, testid: "nav-recebiveis" },
  { to: "/estoque", label: "Estoque", icon: Package, testid: "nav-estoque" },
  { to: "/a-pagar", label: "A Pagar", icon: Receipt, testid: "nav-apagar" },
  { to: "/saldos", label: "Saldos", icon: Wallet, testid: "nav-saldos" },
  { to: "/pagamentos", label: "Pagamentos", icon: CreditCard, testid: "nav-pagamentos" },
  { to: "/clientes", label: "Cedentes/Clientes", icon: Briefcase, testid: "nav-clientes" },
  { to: "/sacados", label: "Sacados", icon: UserCheck, testid: "nav-sacados" },
  { to: "/comissionamento", label: "Comissionamento", icon: DollarSign, testid: "nav-comissionamento" },
  { type: "divider", label: "Integração", testid: "nav-integracao-divider", showIf: ["admin"] },
  { to: "/integracao/policy", label: "Minha Policy", icon: FileText, testid: "nav-policy", roles: ["admin"] },
  { to: "/integracao/tokens", label: "Tokens API", icon: Key, testid: "nav-tokens", roles: ["admin"] },
  { type: "divider", label: "Gestão", testid: "nav-gestao-divider" },
  { to: "/usuarios", label: "Usuários", icon: Users, testid: "nav-usuarios", roles: ["admin", "operator"] },
  { to: "/perfil", label: "Perfil & 2FA", icon: UserCog, testid: "nav-perfil", pendenciaDocs: true },
];

export default function Sidebar({ open, onClose }) {
  const { user } = useAuth();
  const items = NAV.filter((n) => {
    if (n.type === "divider") {
      if (n.showIf) return n.showIf.includes(user?.role);
      return true;
    }
    return !n.roles || n.roles.includes(user?.role);
  });

  return (
    <>
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-white border-r border-[var(--zemo-border)]
          flex flex-col transform transition-transform duration-200 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="app-sidebar"
      >
        {/* Brand */}
        <div className="px-6 py-5 border-b border-[var(--zemo-border)] flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[#0055FF] text-white flex items-center justify-center font-bold tracking-tight">
            Z
          </div>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold tracking-tight">Portal Zemo</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium">Parceiros</div>
          </div>
        </div>

        {/* Partner identity */}
        <div className="px-6 py-4 border-b border-[var(--zemo-border)]">
          <div className="zemo-overline mb-1.5">Parceiro</div>
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-zinc-100 border border-[var(--zemo-border)] flex items-center justify-center text-xs font-bold text-zinc-700">
              {user?.partner_initials || "AF"}
            </div>
            <div className="text-sm font-semibold text-zinc-900">{user?.partner_name || "—"}</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {items.map((item, idx) => {
            if (item.type === "divider") {
              return (
                <div key={item.testid || idx} className="pt-4 pb-1.5 px-3" data-testid={item.testid}>
                  <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-zinc-400">
                    {item.label}
                  </span>
                </div>
              );
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => onClose && onClose()}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors
                  ${isActive ? "nav-active" : "text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"}`
                }
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
                <span>{item.label}</span>
                {item.pendenciaDocs && <PendenciaDot testid={`${item.testid}-pendencia-dot`} className="ml-auto" />}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--zemo-border)] flex items-center gap-2 text-[11px] text-zinc-500">
          <Shield className="h-3.5 w-3.5" />
          <span>Conexão segura · TLS 1.3</span>
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
    </>
  );
}
